import { sha1 } from 'object-hash';
import { ArrayHelper } from './array.helper';

const RGB_COLORS: string[] = [
  '0,150,136',
  '0,188,212',
  '103,58,183',
  '121,85,72',
  '139,195,74',
  '156,39,176',
  '205,220,57',
  '233,30,99',
  '244,67,54',
  '255,152,0',
  '255,193,7',
  '255,235,59',
  '255,87,34',
  '3,169,244',
  '33,150,243',
  '63,81,181',
  '76,175,80',
  '96,125,139',
];

export interface INumberTally {
  [key: string]: number[];
}

export interface INumberDict {
  [key: string]: number;
}

export class MiscHelper {
  // consistently maps strings to the same color every time
  static getRGB = (value: string) =>
    RGB_COLORS[MiscHelper.hashCode(value) % RGB_COLORS.length];

  // maps a string to a non-negative integer
  static hashCode = (value: string): number => {
    let output = 0;

    for (let i = 0; i < value.length; i++) {
      // "| 0" forces the result to be a 32-bit integer for perf gains
      output = (Math.imul(31, output) + value.charCodeAt(i)) | 0;
    }

    return Math.abs(output);
  };

  /** delay execution in sync functions */
  static sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

  // ! deprecate MiscHelper alias
  static saveAs = MiscHelper.saveBlob;

  static saveBlob(blob: Blob, fileNameWithExt: string) {
    const blobUrl = window.URL.createObjectURL(blob);

    const anchor = document.createElement('a');
    anchor.style.display = 'none';
    anchor.href = blobUrl;
    anchor.download = fileNameWithExt;
    document.body.appendChild(anchor);

    // trigger the download
    anchor.click();

    setTimeout(() => {
      window.URL.revokeObjectURL(blobUrl);
      anchor.remove();
    }, 1_000);
  }

  static async saveUrl(url: string, fileNameWithExt: string) {
    const response = await fetch(url);
    const blob = await response.blob();
    MiscHelper.saveBlob(blob, fileNameWithExt);
  }

  static asOrdinal = (n: number): string => {
    if ([11, 12, 13].includes(n)) {
      return n + 'th';
    }

    const m = n % 10;
    switch (m) {
      case 1: {
        return n + 'st';
      }

      case 2: {
        return n + 'nd';
      }

      case 3: {
        return n + 'rd';
      }

      default: {
        return n + 'th';
      }
    }
  };

  static hashify = (input: any): string | undefined => {
    try {
      if (typeof input === 'object') {
        return sha1(JSON.stringify(input));
      }

      if (typeof input === 'string') {
        return sha1(input);
      }

      /** attempt a dirty conversion */
      return sha1(input ?? '');
    } catch (e) {
      console.error(e);
      return undefined;
    }
  };

  static getMean = (values: number[]): number => {
    if (values.length === 0) {
      return 0;
    }

    return values.reduce((prev, curr) => prev + curr, 0) / values.length;
  };

  // returns 0 if you give it an empty list (don't do that)
  static getMax = (values: number[]): number => {
    if (values.length === 0) {
      return 0;
    }

    return values.reduce((a, b) => (a > b ? a : b));
  };

  static getMedian = (values: number[]): number => {
    if (values.length === 0) {
      return 0;
    }

    const sortedValues = [...values].sort((a, b) => (a < b ? -1 : 1));
    const index = Math.floor(sortedValues.length / 2);

    return sortedValues.length % 2 === 0
      ? (sortedValues[index - 1] + sortedValues[index]) / 2
      : sortedValues[index];
  };

  // returns 0 if you give it an empty list (don't do that)
  static getMin = (values: number[]): number => {
    if (values.length === 0) {
      return 0;
    }

    return values.reduce((a, b) => (a < b ? a : b));
  };

  static getStDevObject = <T>(input: T[]): Partial<T> => {
    if (!input || input.length === 0) {
      return {};
    }

    const tally: INumberTally = {};

    input.forEach((item: any) => {
      /** compile all the attribute values from each item */
      Object.keys(item)
        .filter((key) => typeof item[key] === 'number')
        .forEach((key) => {
          if (!tally[key]) {
            tally[key] = [];
          }

          tally[key].push(item[key]);
        });
    });

    const result: INumberDict = {};

    Object.keys(tally).forEach((key) => {
      /** compute the average value for each key */
      const list = tally[key];
      if (list.length > 0) {
        const avg = MiscHelper.getMean(list);
        const stdev = Math.sqrt(
          list.map((x) => Math.pow(x - avg, 2)).reduce((a, b) => a + b) /
            list.length
        );
        result[key] = stdev;
      }
    });

    return result as unknown as Partial<T>;
  };

  // applies the provided reduction fn to each property (where possible) of input values
  private static reduceObject = <T>(
    input: T[],
    reduceFn: (value: any[]) => any
  ): Partial<T> => {
    if (input.length === 0) {
      return {};
    }

    const tally = {} as { [key: string]: any[] };

    input.forEach((item) => {
      /** compile all the attribute values from each item */
      for (const key in item) {
        if (!tally[key]) {
          tally[key] = [];
        }
        tally[key].push(item[key]);
      }
    });

    const result = {} as { [key: string]: any };
    Object.keys(tally).forEach((key) => {
      /** compute the reduction for each key */
      result[key] = reduceFn(tally[key]);
    });

    return result as T;
  };

  static getMinObject = <T>(input: T[]): Partial<T> =>
    MiscHelper.reduceObject(input, MiscHelper.getMin);

  static getMaxObject = <T>(input: T[]): Partial<T> =>
    MiscHelper.reduceObject(input, MiscHelper.getMax);

  static getMedianObject = <T>(input: T[]): Partial<T> =>
    MiscHelper.reduceObject(input, MiscHelper.getMedian);

  static getMeanObject = <T>(input: T[]): Partial<T> =>
    MiscHelper.reduceObject(input, MiscHelper.getMean);

  static getSortedObject(value: any): any {
    if (!value) {
      return value;
    }

    // array
    if (Array.isArray(value)) {
      return value.map(MiscHelper.getSortedObject);
    }

    // object
    if (value !== null && typeof value === 'object') {
      const output: Record<string, any> = {};

      const sortedKeys = Object.keys(value).sort();

      for (const key of sortedKeys) {
        output[key] = MiscHelper.getSortedObject(value[key]);
      }

      return output;
    }

    // primitive
    return value;
  }

  static sanitizeKey(v: string): string {
    return (
      v
        // case-insensitive
        .toLowerCase()
        // expands diacritics and merged chars (like \uFB01)
        .normalize('NFKD')
        // removes diacritics
        .replace(/[\u0300-\u036f]/g, '')
        // recomposes remaining codepoints (e.g. Hangul jamo -> precomposed syllables)
        .normalize('NFC')
        .replace(',', '')
    );
  }

  static keyify(v: string): string[] {
    const values = MiscHelper.sanitizeKey(v).split(' ');

    // also removes empty/undefined strings
    return ArrayHelper.unique(values);
  }
}
